import { logger } from '@forest/logger';
import { Button } from '@ophelos/ui/components/button';
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@ophelos/ui/components/empty';
import { ServerCrash } from 'lucide-react';
import { Component, type ReactNode } from 'react';

interface GlobalErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface GlobalErrorBoundaryState {
  hasError: boolean;
  errorId: string | null;
}

/**
 * Global error boundary that catches React rendering errors and reports them to Datadog RUM.
 *
 * Wrap your application root with this component to capture and report all unhandled React errors.
 * Displays a user-friendly error UI with an error ID for support reference.
 *
 * @example
 * ```tsx
 * <GlobalErrorBoundary>
 *   <App />
 * </GlobalErrorBoundary>
 * ```
 */
export class GlobalErrorBoundary extends Component<GlobalErrorBoundaryProps, GlobalErrorBoundaryState> {
  constructor(props: GlobalErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      errorId: null,
    };
  }

  static getDerivedStateFromError(_error: Error): Partial<GlobalErrorBoundaryState> {
    // Generate a unique error ID for support reference
    const errorId = `err_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    return {
      hasError: true,
      errorId,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Report error to Datadog RUM via the logger
    logger.error('React component error', error, {
      componentStack: errorInfo.componentStack,
      errorId: this.state.errorId,
      errorBoundary: 'GlobalErrorBoundary',
      traceId: logger.getTraceId(),
    });
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Render custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="flex min-h-screen items-center justify-center bg-background">
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="default">
                <div className="p-4.25 border rounded-[2.75rem] bg-accent/25 border-ring/8">
                  <div className="p-3.75 border rounded-[1.75rem] shadow-lg bg-accent/60 border-ring/15 shadow-ring/10">
                    <div className="p-2.75 border rounded-xl shadow-lg bg-card/80 border-ring/35 shadow-ring/15">
                      <ServerCrash className="text-accent-foreground" />
                    </div>
                  </div>
                </div>
              </EmptyMedia>
              <EmptyTitle>Something went wrong</EmptyTitle>
              <EmptyDescription>We&apos;ve been notified and are working to fix this issue.</EmptyDescription>
              <EmptyDescription>Error ID: {this.state.errorId}</EmptyDescription>
            </EmptyHeader>
            <Button onClick={this.handleReload}>Reload Page</Button>
          </Empty>
        </div>
      );
    }

    return this.props.children;
  }
}
